import React, { useContext, useEffect } from 'react';
import Raven from 'raven-js';
import AppContext from '../../context';
import { ContextName } from '../contextName';
import { AccuracyEnum } from '../location';
import { getGooglePlaceFromCoordinates } from '../../utils/geoLocation';
import { sendIpstackRequest } from '../../utils/ipstack';
import { memoizePromise } from '../../utils/memoizePromise';
import { Logger } from '../../utils/logger';

const memoizedIpStackRequest = memoizePromise(sendIpstackRequest);

export const getBrowserLocation = async () => {
  const returnCoords = async (pos, resolve) => {
    const {
      coords: { latitude, longitude },
    } = pos;

    const { name } = await getGooglePlaceFromCoordinates(
      window.google,
      pos.coords,
    );

    resolve({
      latitude,
      longitude,
      accuracy: AccuracyEnum.BROWSER,
      locationName: name,
    });
  };

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => returnCoords(pos, resolve),
      (err) => reject(err),
    );
  });
};

const getIpstackLocation = async () => {
  const result = await memoizedIpStackRequest();
  const { latitude, longitude, city } = result;

  return {
    latitude,
    longitude,
    location: city,
    accuracy: AccuracyEnum.IP_STACK,
  };
};

const canQueryBrowserLocation = async () => {
  try {
    const result = await navigator?.permissions?.query({ name: 'geolocation' });
    const { state } = result;

    return state === 'granted';
  } catch (e) {
    Logger.error(`[ERROR]: ${e}`);
    return false;
  }
};

const canRequestBrowserLocation = async () => {
  try {
    const result = await navigator?.permissions?.query({ name: 'geolocation' });
    const { state } = result;

    return state !== 'denied';
  } catch (e) {
    return false;
  }
};

const setLocation = async (updateCoords, updateName) => {
  if (await canQueryBrowserLocation()) {
    const { locationName, ...location } = await getBrowserLocation();
    updateCoords(location);
    updateName({ locationName });
  } else {
    const location = await getIpstackLocation();
    const { location: locationName } = location;
    updateCoords(location);
    updateName({ locationName });
  }
};

const LocationProvider = ({ children }) => {
  const {
    [ContextName.LOCATION.LATITUDE]: latitude,
    [ContextName.LOCATION.LONGITUDE]: longitude,
    [ContextName.LOCATION.UPDATE_COORDS]: updateCoords,
    [ContextName.LOCATION.UPDATE_NAME]: updateName,
    [ContextName.LOCATION
      .UPDATE_BROWSER_GEO_QUERYABLE]: updateBrowserGeoQueryable,
  } = useContext(AppContext);

  useEffect(() => {
    (async () => {
      updateBrowserGeoQueryable(await canRequestBrowserLocation());
    })();

    if (!latitude || !longitude) {
      try {
        setLocation(updateCoords, updateName);
      } catch (e) {
        Raven.captureException(e);
      }
    }
  }, []);

  return <>{children}</>;
};

export default LocationProvider;
